import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePrinter } from '../../contexts/PrinterContext';
import PrinterForm from './PrinterForm';
import PrinterDiscovery from './PrinterDiscovery';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiPrinter, FiWifi, FiWifiOff, FiPlay, FiSearch } = FiIcons;

const PrinterSettings = () => {
  const { printers, deletePrinter, testPrinter } = usePrinter();
  const [showForm, setShowForm] = useState(false);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [testingPrinter, setTestingPrinter] = useState(null);
  const [discoveredPrinter, setDiscoveredPrinter] = useState(null);

  const handleEdit = (printer) => {
    setEditingPrinter(printer);
    setShowForm(true);
  };

  const handleDelete = (printerId) => {
    if (window.confirm('Are you sure you want to delete this printer?')) {
      deletePrinter(printerId);
    }
  };

  const handleTest = async (printer) => {
    setTestingPrinter(printer.id);
    const result = await testPrinter(printer.id);
    setTestingPrinter(null);
    
    if (result.success) {
      alert('Printer test successful!');
    } else {
      alert('Printer test failed: ' + result.message);
    }
  };

  const handlePrinterDiscovered = (printerData) => {
    setDiscoveredPrinter(printerData);
    setShowDiscovery(false);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setShowDiscovery(false);
    setEditingPrinter(null);
    setDiscoveredPrinter(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Printer Settings</h2>
          <p className="text-warm-brown/70">Manage your receipt printers</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDiscovery(true)}
            className="flex items-center gap-2 px-4 py-2 border border-warm-brown text-warm-brown rounded-lg hover:bg-warm-brown hover:text-cream transition-colors"
          >
            <SafeIcon icon={FiSearch} className="text-sm" />
            Discover Printers
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="text-sm" />
            Add Manually
          </button>
        </div>
      </div>

      {/* Printers Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {printers.map((printer, index) => (
          <motion.div
            key={printer.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-cream p-3 rounded-full">
                    <SafeIcon icon={FiPrinter} className="text-warm-brown text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-playfair text-warm-brown font-medium">
                      {printer.name}
                    </h3>
                    <p className="text-warm-brown/70 text-sm capitalize">{printer.type} Printer</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  printer.isConnected 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  <SafeIcon icon={printer.isConnected ? FiWifi : FiWifiOff} className="text-xs" />
                  {printer.isConnected ? 'Online' : 'Offline'}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {printer.type !== 'usb' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-warm-brown/70">IP Address:</span>
                      <span className="text-warm-brown font-mono">{printer.ipAddress}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-warm-brown/70">Port:</span>
                      <span className="text-warm-brown">{printer.port}</span>
                    </div>
                  </>
                )}
                {printer.description && (
                  <div className="flex justify-between text-sm">
                    <span className="text-warm-brown/70">Description:</span>
                    <span className="text-warm-brown text-right">{printer.description}</span>
                  </div>
                )}
                {printer.lastTestAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-warm-brown/70">Last Test:</span>
                    <span className="text-warm-brown">
                      {new Date(printer.lastTestAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleTest(printer)}
                  disabled={testingPrinter === printer.id}
                  className="flex-1 bg-blue-50 text-blue-600 py-2 px-3 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <SafeIcon icon={FiPlay} className="text-sm" />
                  {testingPrinter === printer.id ? 'Testing...' : 'Test'}
                </button>
                <button
                  onClick={() => handleEdit(printer)}
                  className="flex-1 bg-warm-brown/10 text-warm-brown py-2 px-3 rounded-lg hover:bg-warm-brown/20 transition-colors flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiEdit2} className="text-sm" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(printer.id)}
                  className="bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center"
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {printers.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiPrinter} className="text-6xl text-warm-brown/30 mx-auto mb-4" />
          <p className="text-warm-brown/70 text-lg">No printers configured</p>
          <p className="text-warm-brown/50 text-sm mb-6">Add a printer to enable order receipt printing</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => setShowDiscovery(true)}
              className="flex items-center gap-2 px-6 py-3 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
            >
              <SafeIcon icon={FiSearch} className="text-sm" />
              Discover Printers
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-6 py-3 border border-warm-brown text-warm-brown rounded-lg hover:bg-warm-brown hover:text-cream transition-colors"
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
              Add Manually
            </button>
          </div>
        </div>
      )}

      {/* Setup Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-800 mb-3">Printer Setup Guide</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Automatic Discovery:</h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• Click "Discover Printers" to auto-scan your network</li>
              <li>• Ensure printers are powered on and connected</li>
              <li>• Works with most ESC/POS compatible printers</li>
              <li>• Supports both WiFi and Ethernet connections</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Manual Setup:</h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• Find printer IP from network settings or admin panel</li>
              <li>• Common ports: 9100 (RAW), 515 (LPR), 631 (IPP)</li>
              <li>• Test connection after adding</li>
              <li>• For USB printers, additional drivers may be needed</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Printer Discovery Modal */}
      {showDiscovery && (
        <PrinterDiscovery
          onPrinterSelect={handlePrinterDiscovered}
          onClose={() => setShowDiscovery(false)}
        />
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <PrinterForm
          printer={editingPrinter || discoveredPrinter}
          onClose={resetForm}
        />
      )}
    </div>
  );
};

export default PrinterSettings;